/**
 * Slideshow autoplay progress bar (Shopify Reformation / Flickity)
 * - Hides dots
 * - Inserts a progress bar
 * - Animates left->right
 * - Duration matches REAL autoplay timing by measuring time between slide changes
 */

(() => {
  function getFlickity(slideshow) {
    if (!window.Flickity || typeof window.Flickity.data !== "function") return null;

    return (
      window.Flickity.data(slideshow) ||
      window.Flickity.data(slideshow.querySelector(".flickity-enabled")) ||
      window.Flickity.data(slideshow.querySelector(".flickity-viewport")) ||
      null
    );
  }

  function ensureProgressBar(slideshow) {
    // Hide dots (if present)
    const dots = slideshow.querySelector(".flickity-page-dots");
    if (dots) dots.style.display = "none";

    let track = slideshow.querySelector(".slideshow-progress");
    if (!track) {
      track = document.createElement("div");
      track.className = "slideshow-progress";
      track.innerHTML = `<div class="slideshow-progress__bar"></div>`;

      // Insert after viewport so it sits above slides
      const viewport = slideshow.querySelector(".flickity-viewport");
      if (viewport && viewport.parentNode) {
        viewport.parentNode.insertBefore(track, viewport.nextSibling);
      } else {
        slideshow.appendChild(track);
      }
    }

    return track.querySelector(".slideshow-progress__bar");
  }

  function getConfiguredAutoplayMs(slideshow, flkty) {
    // Prefer Flickity option
    const ap = flkty?.options?.autoPlay;
    if (typeof ap === "number" && ap > 0) return ap;
    if (ap === true) return 3000;

    // Then your markup: data-autoplay="6000"
    const dataAp = Number(slideshow.getAttribute("data-autoplay"));
    if (Number.isFinite(dataAp) && dataAp > 0) return dataAp;

    // Then CSS var: --dot-speed: 6s;
    const css = getComputedStyle(slideshow).getPropertyValue("--dot-speed").trim();
    if (css.endsWith("s")) {
      const secs = parseFloat(css);
      if (Number.isFinite(secs) && secs > 0) return secs * 1000;
    }

    return 6000;
  }

  function attach(slideshow) {
    if (slideshow.dataset.progressBound === "true") return;
    slideshow.dataset.progressBound = "true";

    const flkty = getFlickity(slideshow);
    if (!flkty) return;

    const bar = ensureProgressBar(slideshow);
    if (!bar) return;

    let raf = null;
    let start = 0;
    let duration = getConfiguredAutoplayMs(slideshow, flkty);

    let lastChangeAt = 0;

    function stop() {
      if (raf) cancelAnimationFrame(raf);
      raf = null;
    }

    function render(t) {
      const p = Math.min((t - start) / duration, 1);
      bar.style.transform = `scaleX(${p})`;
      if (p < 1) raf = requestAnimationFrame(render);
    }

    function restart(withDuration) {
      stop();
      duration = withDuration || duration;
      bar.style.transform = "scaleX(0)";
      start = performance.now();
      raf = requestAnimationFrame(render);
    }

    // Start immediately
    restart(duration);

    // When Flickity changes slide, restart with the *measured* real interval
    flkty.on("change", () => {
      const now = performance.now();

      if (lastChangeAt) {
        // Measure actual interval between slide changes
        const measured = now - lastChangeAt;

        // Guardrails: ignore weird tiny/huge values
        const fallback = getConfiguredAutoplayMs(slideshow, flkty);
        const nextDuration =
          measured > 500 && measured < 30000 ? measured : fallback;

        restart(nextDuration);
      } else {
        // First change: use configured duration
        restart(getConfiguredAutoplayMs(slideshow, flkty));
      }

      lastChangeAt = now;
    });

    // If user drags/clicks arrows, restart with configured autoplay duration
    flkty.on("dragStart", () => restart(getConfiguredAutoplayMs(slideshow, flkty)));
    flkty.on("staticClick", () => restart(getConfiguredAutoplayMs(slideshow, flkty)));
  }

  function init() {
    document
      .querySelectorAll("slide-show.slideshow.main-slideshow")
      .forEach(attach);
  }

  document.addEventListener("DOMContentLoaded", init);
  document.addEventListener("shopify:section:load", init);
})();